import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Row, Col, Table, Input, Select, Button, Tooltip } from "antd";
import { SearchOutlined, EyeOutlined, UploadOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { getGroup } from "redux/actions/group.action";
import { getFacility } from "redux/actions/facility.action";
import { getApiArrayDetails } from "utils/MyMUtils";
import { getApi } from "utils/apiCall";
import ViewStaffModal from "./ViewStaffModal";
import ComplianceUploadModal from "./ComplianceUploadModal";

const { Option } = Select;

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

const STATUS_FILTER_OPTIONS = [
  { value: "compliant", label: "Compliant" },
  { value: "non-compliant", label: "Non-Compliant" },
  { value: "attention", label: "Attention" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Asepsis apps show dates as DD-MM-YYYY.
const toDdMmYyyy = (d) => `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return toDdMmYyyy(d);
};

// Every document column shows the same words (Missing, Expired, Expiring
// Soon, Uploaded); the expiry date, when there is one, is in the tooltip.
const getDocStatus = (doc) => {
  const expiry = doc ? formatDate(doc.expiry_date) : null;
  if (!doc || doc.status === "missing") return { text: "Missing", color: "#C0392B" };
  if (doc.status === "expired") return { text: "Expired", color: "#C0392B", tip: expiry ? `Expired on ${expiry}` : null };
  if (doc.status === "expiring_soon") return { text: "Expiring Soon", color: "#B4690E", tip: expiry ? `Expires on ${expiry}` : null };
  return { text: "Uploaded", color: "#1A8F5C", tip: expiry ? `Expires on ${expiry}` : null };
};

const getOverallColor = (apiColor, status) => {
  if (apiColor && apiColor !== "green" && apiColor !== "red" && apiColor !== "orange") return apiColor;
  // map plain color names to hex
  if (apiColor === "green")  return "#1A8F5C";
  if (apiColor === "red")    return "#C0392B";
  if (apiColor === "orange") return "#B4690E";
  const s = String(status || "").toLowerCase();
  if (s === "compliant")   return "#1A8F5C";
  if (s === "attention")   return "#B4690E";
  if (s.includes("non"))   return "#C0392B";
  return "#5B6472";
};

// API response: result.data[], total from result.total_count
const parseListingResponse = (result) => {
  const payload = result?.result ?? result ?? {};
  const items = Array.isArray(payload?.data) ? payload.data : [];
  const total = payload?.total_count ?? payload?.total ?? items.length;
  return { items, total };
};

const mapComplianceRecord = (item) => ({
  id: item.id,
  staffName: item.full_name || `${item.first_name || ""} ${item.last_name || ""}`.trim() || "Unnamed Staff",
  facility: (item.facilities || []).map((f) => f.facility_name).filter(Boolean).join(", ") || "—",
  flu:    getDocStatus(item.flu_vaccination),
  covid:  getDocStatus(item.covid_vaccination),
  stat:   getDocStatus(item.statutory_declaration),
  police: getDocStatus(item.police_check),
  overallStatus: item.overall_status || "Unknown",
  overallStatusColor: getOverallColor(item.overall_status_color, item.overall_status),
});

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatusCell = memo(({ text, color, tip }) => {
  const cell = <span style={{ color, fontWeight: 500, fontSize: 13, whiteSpace: "nowrap" }}>{text}</span>;
  return tip ? <Tooltip title={tip}>{cell}</Tooltip> : cell;
});

// ─── Main component ───────────────────────────────────────────────────────────

const ComplianceTab = ({ onDataChange, onFiltersChange }) => {
  const dispatch = useDispatch();
  const groupList      = useSelector((state) => getApiArrayDetails(state.GroupList.data, []));
  const facilitiesList = useSelector((state) => getApiArrayDetails(state.FacilitiesList.data, []));

  const [searchText, setSearchText]             = useState("");
  const [selectedGroup, setSelectedGroup]       = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [selectedStatus, setSelectedStatus]     = useState(null);
  const [complianceList, setComplianceList]     = useState([]);
  const [tableLoading, setTableLoading]         = useState(false);
  const [debouncedSearch, setDebouncedSearch]   = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 });
  const [viewStaffId, setViewStaffId]           = useState(null);
  const [viewVisible, setViewVisible]           = useState(false);
  const [uploadRecord, setUploadRecord]         = useState(null);
  const [uploadVisible, setUploadVisible]       = useState(false);
  const [refreshKey, setRefreshKey]             = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchText]);

  useEffect(() => { dispatch(getGroup()); }, [dispatch]);

  useEffect(() => {
    setSelectedFacility(null);
    if (selectedGroup) dispatch(getFacility(selectedGroup));
  }, [dispatch, selectedGroup]);

  useEffect(() => {
    setPagination((prev) => (prev.current === 1 ? prev : { ...prev, current: 1 }));
    if (typeof onFiltersChange === "function") {
      onFiltersChange({
        name: debouncedSearch.trim() || undefined,
        facilityId: selectedFacility || undefined,
        groupId: selectedGroup || undefined,
        status: selectedStatus || undefined,
      });
    }
  }, [debouncedSearch, selectedFacility, selectedGroup, selectedStatus]);

  const listingQuery = useMemo(() => {
    const params = new URLSearchParams({
      page: String(pagination.current),
      limit: String(pagination.pageSize),
    });
    if (debouncedSearch.trim()) params.set("name", debouncedSearch.trim());
    if (selectedFacility)       params.set("facility_id", selectedFacility);
    if (selectedGroup)          params.set("org_id", selectedGroup);
    if (selectedStatus)         params.set("status", selectedStatus);
    return params.toString();
  }, [pagination.current, pagination.pageSize, debouncedSearch, selectedFacility, selectedGroup, selectedStatus]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setTableLoading(true);
      const { error, result } = await getApi(`/api/staff/listing?${listingQuery}`);
      if (!isMounted) return;
      if (error) {
        setComplianceList([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        toast.error("Unable to load compliance data.");
        setTableLoading(false);
        return;
      }
      const { items, total } = parseListingResponse(result);
      setComplianceList(items.map(mapComplianceRecord));
      if (typeof onDataChange === "function") {
        onDataChange(items.map(mapComplianceRecord));
      }
      setPagination((prev) => ({ ...prev, total: Number(total) || 0 }));
      setTableLoading(false);
    };
    fetchData();
    return () => { isMounted = false; };
  }, [listingQuery, refreshKey]);

  const handleTableChange = useCallback((next) => {
    setPagination((prev) => ({
      ...prev,
      current: next.current || prev.current,
      pageSize: next.pageSize || prev.pageSize,
    }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const columns = useMemo(() => [
    {
      title: "Staff Name",
      dataIndex: "staffName",
      key: "staffName",
      sorter: (a, b) => a.staffName.localeCompare(b.staffName),
    },
    {
      title: "Facility",
      dataIndex: "facility",
      key: "facility",
    },
    {
      title: "Flu",
      dataIndex: "flu",
      key: "flu",
      render: (v) => <StatusCell text={v.text} color={v.color} tip={v.tip} />,
    },
    {
      title: "Covid",
      dataIndex: "covid",
      key: "covid",
      render: (v) => <StatusCell text={v.text} color={v.color} tip={v.tip} />,
    },
    {
      title: "Stat Declaration",
      dataIndex: "stat",
      key: "stat",
      render: (v) => <StatusCell text={v.text} color={v.color} tip={v.tip} />,
    },
    {
      title: "Police Check",
      dataIndex: "police",
      key: "police",
      render: (v) => <StatusCell text={v.text} color={v.color} tip={v.tip} />,
    },
    {
      title: "Overall Status",
      dataIndex: "overallStatus",
      key: "overallStatus",
      render: (text, record) => <StatusCell text={text} color={record.overallStatusColor} />,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <span style={{ display: "inline-flex", gap: 8 }}>
          <Tooltip title="View">
            <Button
              size="small"
              style={{ borderColor: "#2f6fe0", color: "#2f6fe0", borderRadius: 6 }}
              onClick={() => { setViewStaffId(record.id); setViewVisible(true); }}
            >
              View
            </Button>
          </Tooltip>
          <Tooltip title="Upload Document">
            <Button
              size="small"
              style={{ borderColor: "#2f6fe0", color: "#2f6fe0", borderRadius: 6 }}
              onClick={() => { setUploadRecord(record); setUploadVisible(true); }}
            >
              Upload
            </Button>
          </Tooltip>
        </span>
      ),
    },
  ], []);

  return (
    <div>
      <div style={{ fontSize: 13, color: "#5B6472", marginBottom: 16 }}>
        Track and update workforce declarations and certificates across all staff.
      </div>

      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        <Col flex="1 1 200px">
          <Input
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            placeholder="Search staff"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
        </Col>
        <Col flex="1 1 160px">
          <Select
            placeholder="Group"
            style={{ width: "100%" }}
            allowClear={!!selectedGroup}
            value={selectedGroup}
            onChange={(val) => setSelectedGroup(val || null)}
          >
            {groupList.map((g) => (
              <Option key={g.org_id} value={g.org_id}>{g.org_name}</Option>
            ))}
          </Select>
        </Col>
        <Col flex="1 1 160px">
          <Select
            placeholder="Facility"
            style={{ width: "100%" }}
            allowClear={!!selectedFacility}
            disabled={!selectedGroup}
            value={selectedFacility}
            onChange={setSelectedFacility}
          >
            {facilitiesList.map((f) => (
              <Option key={f.facility_id} value={f.facility_id}>{f.facility_name}</Option>
            ))}
          </Select>
        </Col>
        <Col flex="0 0 140px">
          <Select
            placeholder="Status"
            style={{ width: "100%" }}
            allowClear={!!selectedStatus}
            value={selectedStatus}
            onChange={setSelectedStatus}
          >
            {STATUS_FILTER_OPTIONS.map((s) => (
              <Option key={s.value} value={s.value}>{s.label}</Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={complianceList}
        rowKey="id"
        size="middle"
        loading={tableLoading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: false,
        }}
        onChange={handleTableChange}
        locale={{ emptyText: "No compliance records found." }}
        style={{ background: "#fff" }}
      />

      <ViewStaffModal
        visible={viewVisible}
        staffId={viewStaffId}
        onClose={() => { setViewVisible(false); setViewStaffId(null); }}
      />

      <ComplianceUploadModal
        visible={uploadVisible}
        staffId={uploadRecord?.id}
        staffName={uploadRecord?.staffName}
        onClose={() => { setUploadVisible(false); setUploadRecord(null); }}
        onUploaded={() => setRefreshKey((k) => k + 1)}
      />
    </div>
  );
};

export default memo(ComplianceTab);
